import { useCallback, useEffect, useState } from 'react';
import { api } from '@services/api';
import type { MFAStatus, TrustedDevice, UserSession } from '../types/profile';

interface ProfileData {
  mfaStatus: MFAStatus | null;
  sessions: UserSession[];
  trustedDevices: TrustedDevice[];
  loading: boolean;
  reload: () => Promise<void>;
}

/** Each endpoint is optional: one being unavailable must not blank the page. */
const fetchOrNull = async <T,>(path: string): Promise<T | null> => {
  try {
    return await api.get<T>(path);
  } catch {
    return null;
  }
};

export function useProfileData(): ProfileData {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);

    const [mfa, sessionList] = await Promise.all([
      fetchOrNull<MFAStatus>('/auth/mfa/status'),
      fetchOrNull<UserSession[]>('/auth/sessions'),
    ]);

    setMfaStatus(mfa);
    setSessions(Array.isArray(sessionList) ? sessionList : []);

    // Reads the value just fetched, not state. The previous version branched on
    // the mfaStatus state it had set moments earlier in the same function, so
    // it was still null on first load and trusted devices never loaded at all.
    if (mfa?.mfa_enabled) {
      const devices = await fetchOrNull<{ devices: TrustedDevice[] }>('/auth/mfa/trusted-devices');
      setTrustedDevices(Array.isArray(devices?.devices) ? devices.devices : []);
    } else {
      setTrustedDevices([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { mfaStatus, sessions, trustedDevices, loading, reload };
}
